
import { mapGetters } from "vuex";
import { defineComponent } from "vue";
import {
  ACADEMY_LICENSE_IDS,
  ACADEMY_SUBSCRIPTION_IDS,
  BASIC_PACKAGE_IDS,
  LITE_PACKAGE_IDS,
  LITE_SUBSCRIPTION_IDS,
  VIP_PACKAGE_IDS,
} from "functions/src/Subscriptions/packageIds";
import { Rank } from "@/store/Ranks/RanksInterfaces";
import TeamMember from "./TeamMember.vue";
import PackageIcon from "../common/PackageIcon.vue";
import {
  doRefreshMember,
  UnilevelMember,
  UnilevelTeam,
} from "@/store/Unilevel/UnilevelInterfaces";

export default defineComponent({
  name: "team",
  components: {
    TeamMember,
    PackageIcon,
  },
  props: {
    team: { required: true, type: Object as () => UnilevelTeam },
    loading: { required: true, type: Boolean },
  },
  data() {
    return {
      nextRankReqPoints: 0,
      showActive: "activeAll",
      showDistributor: "distributorAll",
      packageFilter: {
        lite: false,
        basic: false,
        vip: false,
        none: false,
      },
      sortOrder: "ALF",
      displayedTeam: {
        leftTeam: new Array<UnilevelTeam>(),
        rightTeam: new Array<UnilevelTeam>(),
      },
    };
  },
  computed: {
    ...mapGetters("ranks", ["rankById"]),
    UserRank(): Rank {
      return this.rankById(this.team?.leader.currentRank);
    },
    currentPoints(): number {
      return this.team?.leader.points.left + this.team?.leader.points.right;
    },
    leftValidPoints(): number {
      return this.team?.leader.points.valid.left;
    },
    rightValidPoints(): number {
      return this.team?.leader.points.valid.right;
    },
    currentValidPoints(): number {
      return this.leftValidPoints + this.rightValidPoints;
    },
    leftPercentage(): string {
      const p =
        (this.leftValidPoints * 100) / this.team?.leader.nextRankRequiredPoints;
      return p.toFixed(2);
    },
    rightPercentage(): string {
      const p =
        (this.rightValidPoints * 100) /
        this.team?.leader.nextRankRequiredPoints;
      return p.toFixed(2);
    },
    displayedLeftTeam(): UnilevelTeam[] {
      return this.team ? [...this.team.leftTeam] : [];
    },
    displayedRightTeam(): UnilevelTeam[] {
      return this.team ? [...this.team.rightTeam] : [];
    },
    hasTeamMembers(): boolean {
      return this.team?.rightTeam.length > 0 || this.team?.leftTeam.length > 0;
    },
    totalTeamMembers(): number {
      return this.team?.rightTeam.length + this.team?.leftTeam.length;
    },
  },
  methods: {
    async onMemberRefresh(
      member: UnilevelMember,
      side: "left" | "right",
      done: () => void
    ) {
      await this.$store.dispatch("unilevel/" + doRefreshMember, {
        leader: this.team.leader,
        member,
        side,
      });
      done();
    },
    filterTeam() {
      this.displayedTeam.leftTeam = [...this.displayedLeftTeam].filter(
        (m: UnilevelTeam) => {
          return this.filterTeamFunction(m);
        }
      );
      this.displayedTeam.rightTeam = [...this.displayedRightTeam].filter(
        (m: UnilevelTeam) => {
          return this.filterTeamFunction(m);
        }
      );
    },
    orderTeam() {
      this.displayedTeam.leftTeam.sort((a, b) => {
        return this.sortTeamFunction(a, b);
      });
      this.displayedTeam.rightTeam.sort((a, b) => {
        return this.sortTeamFunction(a, b);
      });
    },
    sortTeamFunction(a: UnilevelTeam, b: UnilevelTeam): number {
      if (this.sortOrder === "ALF") {
        return a.leader.username.localeCompare(b.leader.username);
      } else if (this.sortOrder.startsWith("RANK")) {
        const aRank = a.leader.currentRankPriority;
        const bRank = b.leader.currentRankPriority;

        if (this.sortOrder === "RANK_TOP") {
          return bRank - aRank;
        } else if (this.sortOrder === "RANK_LOW") {
          return aRank - bRank;
        }
      } else if (this.sortOrder === "POINTS_TOP") {
        return b.leader.points.qualifying - a.leader.points.qualifying;
      } else if (this.sortOrder === "POINTS_LOW") {
        return a.leader.points.qualifying - b.leader.points.qualifying;
      } else if (this.sortOrder.startsWith("ACADEMY")) {
        // Los días de academia, mirando las DOS generaciones de doc-id: quien
        // haya renovado con el nombre nuevo se ordenaba como si tuviera 0 días.
        const academyDays = (m: UnilevelTeam) =>
          Math.max(
            ...ACADEMY_SUBSCRIPTION_IDS.map(
              (id) => m.leader.subscriptions.get(id) ?? 0
            )
          );
        const aDays = academyDays(a);
        const bDays = academyDays(b);
        if (this.sortOrder === "ACADEMY_CLOSE") {
          return aDays - bDays;
        } else if (this.sortOrder === "ACADEMY_DISTANT") {
          return bDays - aDays;
        }
      }
      return 0;
    },
    getMemberRemainingDays(m: UnilevelTeam): number {
      const pkg = m.leader.subscriptionPackage?.package?.packageId;
      const ids = LITE_PACKAGE_IDS.includes(pkg ?? "")
        ? LITE_SUBSCRIPTION_IDS
        : ACADEMY_SUBSCRIPTION_IDS;
      return Math.max(...ids.map((id) => m.leader.subscriptions.get(id) ?? 0));
    },
    /**
     * Los días de LICENCIA que mira el filtro "Distribuidor": la de siempre O la
     * GLOBAL, y manda la que más días deje.
     *
     * ⚠️ SIN RAMA POR PAQUETE, Y ES A PROPÓSITO. Este filtro leía
     * `distributor-license` para TODO EL MUNDO, Lites incluidos — es decir, un
     * Lite salía siempre en "sin licencia". Emparejarlo aquí por paquete
     * (`LITE_LICENSE_IDS` para los Lite) sería más correcto, pero MOVERÍA a
     * gente de una pestaña a otra, y este cambio es ADITIVO: sólo se suma la
     * global. Si se quiere emparejar, es una decisión de producto aparte.
     *
     * Devuelve `undefined` cuando no tiene NINGUNA, porque la rama
     * `distributorOn` distingue "no tiene" de "tiene 0".
     */
    getMemberLicenseDays(m: UnilevelTeam): number | undefined {
      const days = ACADEMY_LICENSE_IDS.map((id) =>
        m.leader.subscriptions.get(id)
      ).filter((d): d is number => d != undefined);
      return days.length > 0 ? Math.max(...days) : undefined;
    },
    filterTeamFunction(m: UnilevelTeam): boolean {
      let responseActive = true;
      let responseDistributor = false;
      let responsePackage = true;

      const days = this.getMemberRemainingDays(m);
      if (this.showActive === "activeOn") {
        responseActive = days > 0;
      } else if (this.showActive === "activeOff") {
        responseActive = days <= 0;
      }

      if (this.showDistributor === "distributorAll") {
        responseDistributor = true;
      } else if (this.showDistributor === "distributorOn") {
        const dDays = this.getMemberLicenseDays(m);
        responseDistributor = dDays && dDays > 0 ? true : false;
      } else if (this.showDistributor === "distributorOff") {
        let dDays = this.getMemberLicenseDays(m);
        dDays = dDays ? dDays : 0;
        responseDistributor = dDays <= 0 ? true : false;
      }

      const anyPkgChecked = Object.values(this.packageFilter).some((v) => v);
      if (anyPkgChecked) {
        const pkg = m.leader.subscriptionPackage?.package?.packageId;
        // Los filtros por paquete aceptan las DOS generaciones de etiqueta: si
        // algún día el backend empieza a escribir `gtr-fly`, el filtro no deja
        // de casar y la lista no se vacía en silencio.
        responsePackage =
          (this.packageFilter.lite && LITE_PACKAGE_IDS.includes(pkg ?? "")) ||
          (this.packageFilter.basic && BASIC_PACKAGE_IDS.includes(pkg ?? "")) ||
          (this.packageFilter.vip && VIP_PACKAGE_IDS.includes(pkg ?? "")) ||
          (this.packageFilter.none && (pkg === "none" || !pkg));
      }

      return responseActive && responseDistributor && responsePackage;
    },
  },
  watch: {
    showActive: function () {
      this.filterTeam();
      this.orderTeam();
    },
    packageFilter: {
      handler() {
        this.filterTeam();
        this.orderTeam();
      },
      deep: true,
    },
    showDistributor: function () {
      this.filterTeam();
      this.orderTeam();
    },
    sortOrder: function () {
      this.filterTeam();
      this.orderTeam();
    },
    displayedLeftTeam: function () {
      this.filterTeam();
      this.orderTeam();
    },
    displayedRightTeam: function () {
      this.filterTeam();
      this.orderTeam();
    },
  },
  mounted() {
    this.filterTeam();
  },
});
